import { createStyles, makeStyles } from "@material-ui/core/styles";
import MuiTextField, { BaseTextFieldProps } from "@material-ui/core/TextField";
import CheckIcon from "@material-ui/icons/CheckRounded";
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteProps } from "@material-ui/lab/Autocomplete";
import { Value as InternalValue } from "@material-ui/lab/useAutocomplete";
import clsx from "clsx";
import React, { Ref, useCallback } from "react";
import { useFocus } from "../../hook";
import { Color } from "../../theme";
import { HasTestId } from "../../types";
import { isArray } from "../../util";
import { TextFieldProps } from "../textField";
import { VirtualizedListBox } from "./VirtualizedListBox";
import { EndAdornment } from "./EndAdornment";

export interface SingleComboBoxProps<ValueT>
    extends Omit<
            AutocompleteProps<ValueT, false, true | undefined, false>,
            | "debug" // <- no longer available in v5.0.0 so remove it already now
            | "defaultValue"
            | "forcePopupIcon"
            | "freeSolo"
            | "getLimitTagsText"
            | "limitTags"
            | "multiple"
            | "onChange"
            | "openText"
            | "renderInput"
            | "renderTags"
            | "value"
        >,
        HasTestId {
    /**
     * Reference to the root element of the underlying Material UI Autocomplete component. We don't use ref forwarding for this since using `React.forwardRef` with generic components causes typing problems.
     */
    autocompleteRef?: Ref<HTMLDivElement>;
    /**
     * Whether the lock icon should be displayed if the field is `disabled`. Defaults to `undefined` which is interpreted as `false`.
     */
    disableLockIcon?: boolean;
    /**
     * Don't display the validation icon. Defaults to `undefined` which is interpreted as `false` (= validation icon is shown if text is not empty depending on `error` property).
     */
    disableValidationIcon?: boolean;
    /**
     * Enables the virtualization of the dropdown list. Defaults to `false`. Use this if you have to render lots of options.
     */
    useListVirtualization?: boolean;
    error?: BaseTextFieldProps["error"];
    helperText?: BaseTextFieldProps["helperText"];
    /**
     * Set it to a valid input variant of the underlying `MuiTextField`. Defaults to `"filled"`.
     */
    inputVariant?: TextFieldProps["variant"];
    label: React.ReactNode;
    /**
     * Callback fired when the value changes.
     *
     * @param event The event source of the callback.
     * @param value The new value of the component.
     * @param reason One of "create-option", "select-option", "remove-option", "blur" and "clear".
     */
    onChange: (value: ValueT | undefined, reason: AutocompleteChangeReason, event?: React.ChangeEvent<unknown>, details?: AutocompleteChangeDetails<ValueT> | undefined) => void;
    placeholder: BaseTextFieldProps["placeholder"];
    /**
     * If `true`, the label is displayed as required and the `input` element` will be required.
     */
    required?: boolean;
    /**
     * Determines whether `Chip`s should be used to show selected items. Default is text, e.g. `(3) Option 2, Option 5, ...` without line breaks.
     */
    value: ValueT | undefined;
}

export const SingleComboBox = <ValueT extends unknown>({
    autocompleteRef,
    ChipProps,
    disabled,
    disableClearable,
    disableLockIcon,
    disableValidationIcon,
    useListVirtualization,
    error,
    // defaults to `(option) => option.label ?? option` in Material-UI 5.0, see https://next.material-ui.com/api/autocomplete/
    getOptionLabel = (option: ValueT) =>
        // eslint-disable-next-line @typescript-eslint/ban-types
        typeof option === "object" && "label" in (option as object) ? ((option as unknown) as { label: string }).label : ((option as unknown) as string),
    helperText,
    inputVariant,
    label,
    onBlur,
    onChange,
    onFocus,
    open,
    openOnFocus = true,
    options,
    placeholder,
    required,
    testId,
    value,
    ...props
}: SingleComboBoxProps<ValueT>): React.ReactElement<SingleComboBoxProps<ValueT>> => {
    const { focus, handleBlur, handleFocus } = useFocus(false, onBlur, onFocus);

    const handleChange = useCallback(
        (
            event: React.ChangeEvent<unknown>,
            value: InternalValue<ValueT, false, true | undefined, false>,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<ValueT> | undefined,
        ) => {
            return onChange(value ?? undefined /* `undefined` instead `null` */, reason, event, details);
        },
        [onChange],
    );

    const classes = useStyles();
    return (
        <Autocomplete<ValueT, false, true | undefined, false>
            ChipProps={{ ...ChipProps, variant: "outlined" }}
            clearOnBlur={false}
            data-testid={testId}
            disableClearable={disabled || disableClearable || !focus || !value || (isArray(value) && value.length === 0) ? true : undefined}
            disabled={disabled}
            forcePopupIcon={!disabled}
            getOptionLabel={getOptionLabel}
            multiple={false}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            open={open}
            openOnFocus={openOnFocus}
            options={options}
            ListboxComponent={useListVirtualization ? VirtualizedListBox : undefined}
            ref={autocompleteRef}
            renderInput={({ InputProps, ...params }) => (
                <MuiTextField
                    {...params}
                    error={error}
                    helperText={helperText || <span />} // at least a tag prevents helper text area from shrinking...
                    InputProps={{
                        ...InputProps,
                        className: clsx(InputProps?.className, { required }),
                        endAdornment: (
                            <EndAdornment
                                className={clsx({ [classes.standardVariantEndAdornment]: inputVariant === "standard" })}
                                disabled={disabled}
                                disableClearButton={disableClearable}
                                disableLockIcon={disableLockIcon}
                                disableValidationIcon={disableValidationIcon}
                                error={error}
                                focus={focus}
                                value={value}
                            >
                                {InputProps.endAdornment}
                            </EndAdornment>
                        ),
                    }}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    variant={inputVariant ?? "filled"}
                />
            )}
            renderOption={(value, { selected }) => {
                const label = getOptionLabel(value);
                return (
                    <>
                        <span key={`${label}-label`} className={classes.optionLabel}>
                            {label}
                        </span>
                        {selected && <CheckIcon key={`${label}-icon`} className={classes.optionSelected} />}
                    </>
                );
            }}
            // default to `null` because otherwise input thinks to be uncontrolled on `undefined` which causes a thrown exception on console by React
            value={value ?? null}
            {...props}
        />
    );
};

const useStyles = makeStyles(
    createStyles({
        optionLabel: {
            color: Color.grey6,
            float: "left",
            width: "100%",
        },
        optionSelected: {
            color: Color.grey5,
            float: "right",
        },
        standardVariantEndAdornment: {
            top: "10px",
            right: 0,
        },
    }),
);
